import { cn } from '../lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';

function hasExplicitAccessibilityProps(props: React.HTMLAttributes<HTMLDivElement>) {
  return (
    props.role !== undefined ||
    props['aria-label'] !== undefined ||
    props['aria-labelledby'] !== undefined ||
    props['aria-hidden'] !== undefined
  );
}

/**
 * Base Skeleton component with dark mode support
 */
function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const ariaHidden =
    props['aria-hidden'] ?? (hasExplicitAccessibilityProps(props) ? undefined : true);

  return (
    <div
      {...props}
      aria-hidden={ariaHidden}
      className={cn('bg-muted animate-pulse rounded-[var(--crm-control-radius)]', className)}
    />
  );
}

/**
 * Text line skeleton with configurable width
 */
function SkeletonText({
  className,
  width = 'full',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  width?: 'full' | '3/4' | '1/2' | '1/4' | '1/3' | '2/3';
}) {
  const widthClasses = {
    full: 'w-full',
    '3/4': 'w-3/4',
    '2/3': 'w-2/3',
    '1/2': 'w-1/2',
    '1/3': 'w-1/3',
    '1/4': 'w-1/4',
  };

  return <Skeleton className={cn('h-4', widthClasses[width], className)} {...props} />;
}

/**
 * Avatar/circle skeleton
 */
function SkeletonAvatar({
  className,
  size = 'md',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  return <Skeleton className={cn('rounded-full', sizeClasses[size], className)} {...props} />;
}

/**
 * Button skeleton
 */
function SkeletonButton({
  className,
  size = 'md',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClasses = {
    sm: 'h-8 w-20',
    md: 'h-10 w-24',
    lg: 'h-12 w-32',
  };

  return (
    <Skeleton
      className={cn('rounded-[var(--crm-control-radius)]', sizeClasses[size], className)}
      {...props}
    />
  );
}

/**
 * Card skeleton with optional header and content lines
 */
function SkeletonCard({
  className,
  lines = 3,
  showHeader = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  lines?: number;
  showHeader?: boolean;
}) {
  return (
    <div
      className={cn(
        'border-border bg-card space-y-4 rounded-[var(--crm-panel-radius)] border p-4',
        className
      )}
      {...props}
    >
      {showHeader && (
        <div className="flex items-center gap-3">
          <SkeletonAvatar size="md" />
          <div className="flex-1 space-y-2">
            <SkeletonText width="1/2" />
            <SkeletonText width="1/4" className="h-3" />
          </div>
        </div>
      )}
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonText key={i} width={i === lines - 1 ? '2/3' : 'full'} />
        ))}
      </div>
    </div>
  );
}

/**
 * Table row skeleton
 */
function SkeletonTableRow({
  className,
  columns = 5,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & {
  columns?: number;
}) {
  return (
    <TableRow className={className} {...props}>
      {Array.from({ length: columns }).map((_, i) => (
        <TableCell key={i}>
          <SkeletonText width={i === 0 ? '3/4' : i === columns - 1 ? '1/2' : 'full'} />
        </TableCell>
      ))}
    </TableRow>
  );
}

/**
 * Full table skeleton for list pages
 */
function SkeletonTable({
  className,
  rows = 5,
  columns = 6,
  showToolbar = true,
  showPagination = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  rows?: number;
  columns?: number;
  showToolbar?: boolean;
  showPagination?: boolean;
}) {
  const role = props.role ?? 'status';
  const ariaLabel =
    props['aria-label'] ??
    (props['aria-labelledby'] || props.role !== undefined ? undefined : 'Loading table content');
  const ariaLive = props['aria-live'] ?? (role === 'status' ? 'polite' : undefined);
  const ariaBusy = props['aria-busy'] ?? (role === 'status' ? true : undefined);

  return (
    <div
      {...props}
      role={role}
      aria-label={ariaLabel}
      aria-live={ariaLive}
      aria-busy={ariaBusy}
      className={cn(
        'border-border bg-card overflow-hidden rounded-[var(--crm-panel-radius)] border',
        className
      )}
    >
      {/* Toolbar */}
      {showToolbar && (
        <div className="border-border flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-48 rounded-[var(--crm-control-radius)]" />
            <Skeleton className="h-9 w-24 rounded-[var(--crm-control-radius)]" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-9 rounded-[var(--crm-control-radius)]" />
            <Skeleton className="h-9 w-20 rounded-[var(--crm-control-radius)]" />
            <Skeleton className="h-9 w-24 rounded-[var(--crm-control-radius)]" />
          </div>
        </div>
      )}

      {/* Table */}
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            {Array.from({ length: columns }).map((_, i) => (
              <TableHead key={i}>
                <SkeletonText width={i === 0 ? '3/4' : '1/2'} className="h-3" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {showPagination && (
        <div className="border-border flex items-center justify-between border-t p-4">
          <div className="flex items-center gap-2">
            <SkeletonText width="1/4" className="w-24" />
            <Skeleton className="h-8 w-16 rounded-[var(--crm-control-radius)]" />
            <SkeletonText width="1/4" className="w-32" />
          </div>
          <div className="flex items-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-8 rounded-[var(--crm-control-radius)]" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * List page skeleton with header, search, and table
 */
function SkeletonListPage({
  className,
  showTitle = true,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
  showTitle?: boolean;
}) {
  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Header */}
      {showTitle && (
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <SkeletonText className="h-8 w-48" />
            <SkeletonText className="h-4 w-64" />
          </div>
          <div className="flex items-center gap-3">
            <SkeletonButton size="md" />
            <SkeletonButton size="md" className="w-32" />
          </div>
        </div>
      )}

      {/* Advanced Search */}
      <Skeleton className="h-12 w-full rounded-[var(--crm-control-radius)]" />

      {/* Table */}
      <SkeletonTable rows={10} columns={6} />
    </div>
  );
}

/**
 * Detail page skeleton with tabs and content sections
 */
function SkeletonDetailPage({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <SkeletonAvatar size="xl" />
          <div className="space-y-2">
            <SkeletonText className="h-8 w-64" />
            <SkeletonText className="h-4 w-48" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SkeletonButton size="md" />
          <SkeletonButton size="md" />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-border flex gap-4 border-b pb-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-20 rounded-[var(--crm-control-radius)]" />
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SkeletonCard lines={4} />
          <SkeletonCard lines={3} />
        </div>
        <div className="space-y-6">
          <SkeletonCard lines={2} showHeader={false} />
          <SkeletonCard lines={5} showHeader={false} />
        </div>
      </div>
    </div>
  );
}

/**
 * Dashboard skeleton with KPI cards and charts
 */
function SkeletonDashboard({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <SkeletonText className="h-8 w-48" />
          <SkeletonText className="h-4 w-64" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-32 rounded-[var(--crm-control-radius)]" />
          <Skeleton className="h-10 w-10 rounded-[var(--crm-control-radius)]" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="border-border bg-card space-y-3 rounded-[var(--crm-panel-radius)] border p-4"
          >
            <div className="flex items-center justify-between">
              <SkeletonText width="1/2" className="h-3" />
              <Skeleton className="h-8 w-8 rounded-[var(--crm-control-radius)]" />
            </div>
            <SkeletonText width="1/3" className="h-8" />
            <SkeletonText width="2/3" className="h-3" />
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border-border bg-card rounded-[var(--crm-panel-radius)] border p-4">
          <SkeletonText width="1/3" className="mb-4 h-5" />
          <Skeleton className="h-64 w-full rounded-[var(--crm-panel-radius)]" />
        </div>
        <div className="border-border bg-card rounded-[var(--crm-panel-radius)] border p-4">
          <SkeletonText width="1/3" className="mb-4 h-5" />
          <Skeleton className="h-64 w-full rounded-[var(--crm-panel-radius)]" />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="border-border bg-card rounded-[var(--crm-panel-radius)] border p-4">
        <SkeletonText width="1/4" className="mb-4 h-5" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <SkeletonAvatar size="sm" />
              <div className="flex-1 space-y-1">
                <SkeletonText width="3/4" />
                <SkeletonText width="1/4" className="h-3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Form skeleton
 */
function SkeletonForm({
  className,
  fields = 4,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  fields?: number;
}) {
  return (
    <div className={cn('space-y-6', className)} {...props}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <SkeletonText width="1/4" className="h-4" />
          <Skeleton className="h-10 w-full rounded-[var(--crm-control-radius)]" />
        </div>
      ))}
      <div className="flex justify-end gap-3 pt-4">
        <SkeletonButton size="md" />
        <SkeletonButton size="md" className="w-28" />
      </div>
    </div>
  );
}

export {
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonButton,
  SkeletonCard,
  SkeletonTableRow,
  SkeletonTable,
  SkeletonListPage,
  SkeletonDetailPage,
  SkeletonDashboard,
  SkeletonForm,
};
